import { z } from 'zod';
import {
  BrandingSchema,
  EventCheckoutSchema,
  EventStatusSchema,
  FormFieldSchema,
  SlugSchema,
} from './config.ts';
import { HttpsUrlSchema } from './organization.ts';

/**
 * Request bodies for the admin write API (Phase 2).
 *
 * Every one of these is `strictObject`: an unknown key is a typo or a stale
 * client, and silently ignoring it would let an admin believe they changed
 * something they did not.
 */

export const OrganizationCreateSchema = z.strictObject({
  name: z.string().min(1).max(200),
  description: z.string().max(2000).nullish(),
  website_url: HttpsUrlSchema.nullish(),
  logo_url: HttpsUrlSchema.nullish(),
});

export const OrganizationUpdateSchema = OrganizationCreateSchema.partial();

export type OrganizationCreate = z.infer<typeof OrganizationCreateSchema>;
export type OrganizationUpdate = z.infer<typeof OrganizationUpdateSchema>;

/**
 * A new event is always created as a draft with `config_version` 1; status,
 * branding, messages and the form are edited afterwards, which is why they
 * are absent here and present in the update schema.
 */
export const EventCreateSchema = z.strictObject({
  name: z.string().min(1).max(200),
  slug: SlugSchema,
  timezone: z.string().min(1).default('Asia/Ho_Chi_Minh'),
  organization_id: z.string().min(1).max(100).nullish(),
  event_url: HttpsUrlSchema.nullish(),
  starts_at: z.iso.datetime({ offset: true }).nullish(),
  ends_at: z.iso.datetime({ offset: true }).nullish(),
  /**
   * Validated here so a misspelled key is a 400 the organizer sees while the
   * form is still open. `EventCheckoutSchema` is strict, and the publish path
   * silently drops a blob it cannot parse; without this the only trace of the
   * typo would be a log line nobody reads.
   */
  checkout: EventCheckoutSchema.optional(),
});

export const EventUpdateSchema = z.strictObject({
  name: z.string().min(1).max(200).optional(),
  slug: SlugSchema.optional(),
  timezone: z.string().min(1).optional(),
  status: EventStatusSchema.optional(),
  organization_id: z.string().min(1).max(100).nullish(),
  event_url: HttpsUrlSchema.nullish(),
  starts_at: z.iso.datetime({ offset: true }).nullish(),
  ends_at: z.iso.datetime({ offset: true }).nullish(),
  branding: BrandingSchema.optional(),
  messages: z.record(z.string(), z.unknown()).optional(),
  form: z.strictObject({ fields: z.array(FormFieldSchema).min(1) }).optional(),
  checkout: EventCheckoutSchema.optional(),
});

export type EventCreate = z.infer<typeof EventCreateSchema>;
/** What a client sends: `timezone` has a default, so it is optional on input. */
export type EventCreateInput = z.input<typeof EventCreateSchema>;
export type EventUpdate = z.infer<typeof EventUpdateSchema>;

/**
 * A location an admin enters by hand. `is_placeholder` is deliberately absent:
 * the placeholder is maintained by the server, never authored.
 */
export const LocationCreateSchema = z.strictObject({
  code: z.string().min(1).max(32),
  name: z.string().min(1).max(200),
  province: z.string().max(200).nullish(),
  aliases: z.array(z.string().min(1).max(200)).default([]),
  sort_order: z.int().optional(),
});

export const LocationUpdateSchema = z.strictObject({
  code: z.string().min(1).max(32).optional(),
  name: z.string().min(1).max(200).optional(),
  province: z.string().max(200).nullish(),
  aliases: z.array(z.string().min(1).max(200)).optional(),
  sort_order: z.int().optional(),
  /** Deactivating is the only removal there is — see decision 7. */
  is_active: z.boolean().optional(),
});

/** Pasted straight out of a spreadsheet: `code,name,province,aliases,sort_order`. */
export const LocationImportSchema = z.strictObject({
  csv: z.string().min(1).max(512_000),
});

export type LocationCreate = z.infer<typeof LocationCreateSchema>;
/** `aliases` defaults to an empty list, so it is optional on input. */
export type LocationCreateInput = z.input<typeof LocationCreateSchema>;
export type LocationUpdate = z.infer<typeof LocationUpdateSchema>;
export type LocationImport = z.infer<typeof LocationImportSchema>;

/**
 * Responses of the admin write API.
 *
 * The rows go out as the database holds them — `*_json` columns as strings,
 * booleans as 0/1 — because the admin editor round-trips them straight back.
 * `looseObject` so a column added by a later migration reaches the editor
 * instead of failing validation in the browser.
 */
export const AdminEventSchema = z.looseObject({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  status: EventStatusSchema,
  timezone: z.string(),
  starts_at: z.string().nullable(),
  ends_at: z.string().nullable(),
  config_version: z.int(),
  form_schema_json: z.string(),
  branding_json: z.string(),
  messages_json: z.string(),
  checkout_json: z.string().nullable(),
  organization_id: z.string().nullable(),
  event_url: z.string().nullable(),
  published_config_version: z.int().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const AdminEventListItemSchema = AdminEventSchema.extend({
  organization_name: z.string().nullable(),
  /** Real locations only — the placeholder is not one the admin created. */
  location_count: z.int(),
  /**
   * Rows in `checkins` for this event.
   *
   * Counted per event rather than derived from the summary endpoint: the home
   * screen lists every event, and one request that answers for all of them
   * beats one request per row.
   */
  checkin_count: z.int(),
});

export const AdminLocationSchema = z.looseObject({
  id: z.string(),
  event_id: z.string(),
  code: z.string(),
  name: z.string(),
  province: z.string().nullable(),
  aliases_json: z.string(),
  sort_order: z.int(),
  is_active: z.int(),
  is_placeholder: z.int(),
  created_at: z.string(),
  updated_at: z.string(),
});

/** What a publish would change, in the operator's language. */
export const SnapshotDiffSchema = z.strictObject({
  event_changes: z.array(z.string()),
  locations_added: z.array(z.string()),
  locations_removed: z.array(z.string()),
  locations_changed: z.array(z.string()),
});

export const PublishPreviewSchema = z.strictObject({
  diff: SnapshotDiffSchema,
  will_publish_version: z.int(),
});

export const PublishResultSchema = z.strictObject({
  published_config_version: z.int(),
  diff: SnapshotDiffSchema,
});

export const LocationImportResultSchema = z.strictObject({
  created: z.int(),
  updated: z.int(),
  errors: z.array(z.string()),
});

/** List forms, exported so a client can validate a response without zod. */
export const AdminEventListSchema = z.array(AdminEventListItemSchema);
export const AdminLocationListSchema = z.array(AdminLocationSchema);

export type AdminEvent = z.infer<typeof AdminEventSchema>;
export type AdminEventListItem = z.infer<typeof AdminEventListItemSchema>;
export type AdminLocation = z.infer<typeof AdminLocationSchema>;
export type SnapshotDiff = z.infer<typeof SnapshotDiffSchema>;
export type PublishPreview = z.infer<typeof PublishPreviewSchema>;
export type PublishResult = z.infer<typeof PublishResultSchema>;
export type LocationImportResult = z.infer<typeof LocationImportResultSchema>;
